// The shared logbook trip's entries: the index child of
// /shared/logbook/:shareId. LogbookTrip's page read-only -- same map, same
// totals, same table, same notes and photo strips -- with the Rename button and
// the Combine row simply not written.
//
// No ItemBreadcrumbs: they name an item the viewer has no access to and no
// business being sent to.
//
// SHOW PHOTOS HERE IS DISPLAY-ONLY, which is the one behavioural difference
// from the owner's page and the one worth knowing before "fixing" it. There,
// the switch is a MOUNT GATE on an S3 listing -- off means the read never
// happens. Here the Lambda already listed and presigned everything in the one
// payload, so there is nothing left to gate: the switch only decides what the
// table draws.

import { Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { lazyWithReload } from '../../utilities/lazyWithReload';
import { DataCard, Spinner } from '../../components';
import { CardHeader } from '../../components/CardHeader';
import PhotoViewer from '../../components/PhotoViewer';
import { urlForSharedLogbookEntry } from '../../fleet-shared/Urls.mjs';
import { tripSummaryParts } from '../../fleet-shared/LogbookTrips.mjs';
import { ENTRY_ROUTE_KEY } from '../../fleet-shared/TripRoute.mjs';
import LogbookEntriesTable from '../logbook/LogbookEntriesTable';
import LogbookViewControls from '../logbook/LogbookViewControls';
import PhotoErrorAlert from '../logbook/PhotoErrorAlert';
import DownloadAllPhotos from './DownloadAllPhotos';
import SharedPhotoUpload from './SharedPhotoUpload';
import { shareUploadAvailable } from '../../utilities/shareUpload.mjs';
import { useSharedLogbookTrip } from './SharedLogbookTrip';

// Lazy: the only import path here that pulls in maplibre-gl (~250 KB gz).
const TripGroupMap = lazyWithReload(() => import('../../components/map/TripGroupMap'), 'TripGroupMap');

const SharedLogbookTripEntries = () => {
  const navigate = useNavigate();
  const {
    shareId, name, typeId, columns, summary, displayEntries, orderedEntries,
    mapLegs, anyOnMap, placeIndex,
    photosByEntry, photosError, photosForDownload, zipName,
    uploadsAllowed, addPhotos,
    showNotes, setShowNotes, showPhotos, setShowPhotos,
  } = useSharedLogbookTrip();

  // Which photo the viewer is showing, and out of which list. The strip is
  // per-row, so the list is that ROW's photos rather than the whole trip's:
  // stepping with the arrows should walk one day, which is what a reader who
  // clicked a thumbnail in that row means by "next".
  const [viewing, setViewing] = useState(null);

  const onSelectEntry = (entry) => navigate(urlForSharedLogbookEntry(shareId, entry.id));

  // A photo opens in the viewer. It used to be `window.location.href =
  // photo.url`, which navigated the SPA away to a raw S3 URL -- so Back re-ran
  // the whole share fetch, and past the presign's one-hour ceiling the
  // destination was an S3 error document. The viewer also gives the visitor
  // somewhere to download the original from.
  const openPhoto = (photo) => {
    for (const entry of displayEntries) {
      const photos = photosByEntry?.get(entry.id) ?? [];
      const index = photos.findIndex((candidate) => candidate.key === photo.key);
      if (index >= 0) {
        setViewing({ photos, index });
        return;
      }
    }
  };

  return (
    <>
      {anyOnMap && (
        <Suspense fallback={<Spinner />}>
          <TripGroupMap
            legs={mapLegs}
            onSelectLeg={onSelectEntry}
            routeKey={ENTRY_ROUTE_KEY}
            title="Route"
            showWeather={false}
          />
        </Suspense>
      )}

      <DataCard
        // No extraActions. Rename belongs to the owner, and Combine changes
        // which trip ten records are in -- read-only here is these controls
        // never being written rather than a flag suppressing them.
        header={<CardHeader text={name} />}
        body={
          <>
            {summary && summary.count > 0 && (
              <p className="text-center text-body-secondary small mb-3" data-testid="trip-totals">
                {tripSummaryParts(summary).join(' · ')}
              </p>
            )}

            {displayEntries.length > 0 && (
              <LogbookViewControls
                showViewToggle={false}
                showNotes={showNotes}
                onShowNotesChange={setShowNotes}
                showPhotosToggle
                showPhotos={showPhotos}
                onShowPhotosChange={setShowPhotos}
              />
            )}

            {/* The same warning and the same dismissal as every other logbook
                list -- this page is the fourth caller, and the whole reason
                that component exists is that a fourth inline copy is where the
                dismissal quietly gets dropped.

                Only the LISTING can fail here, so the error is always `list`
                shaped: there is no per-photo open request to fail, because a
                dead presigned URL is AttachmentImage's own inline glyph. The
                object is memoized so the dismissal, which keys on identity,
                isn't undone by the next render. */}
            <PhotoErrorAlert error={photosError} show={showPhotos} />

            {/* "For pages that don't [have a photo header], it can be a link
                near the photos." The card's own header is the TRIP's -- Rename
                and Combine belong to the owner and are simply not written here
                -- and the photos live inside the table's continuation rows, so
                this sits with the view switches, which is the nearest thing to
                a photo header this page has.

                Gated on the switch as well as on there being photos: an offer
                to zip pictures nobody is looking at is noise, and it would be
                offering to download what the table is not showing. */}
            {showPhotos && (
              <div className="mb-3">
                <DownloadAllPhotos
                  variant="link"
                  groups={photosForDownload.groups}
                  withheld={photosForDownload.withheld}
                  zipName={zipName}
                />
              </div>
            )}

            <LogbookEntriesTable
              entries={displayEntries}
              columns={columns}
              placeIndex={placeIndex}
              showNotes={showNotes}
              onSelectEntry={onSelectEntry}
              photosByEntry={showPhotos ? photosByEntry : null}
              onOpenPhoto={openPhoto}
            />
          </>
        }
      />

      {/* Gated TWICE: on the deploy, because a branch without the upload backend
          has no endpoint to call, and on the owner's per-link toggle. Photos are
          stored per entry, so this one carries the day picker. */}
      {shareUploadAvailable() && uploadsAllowed && (
        <SharedPhotoUpload
          shareId={shareId}
          entries={orderedEntries}
          typeId={typeId}
          placeIndex={placeIndex}
          onAdded={addPhotos}
        />
      )}

      <PhotoViewer
        photos={viewing?.photos}
        index={viewing?.index}
        onIndex={(index) => setViewing((current) => ({ ...current, index }))}
        onHide={() => setViewing(null)}
      />
    </>
  );
};

export default SharedLogbookTripEntries;
